import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuLogOut,
  LuPhone,
  LuSettings,
  LuUser,
  LuUserCircle,
} from "react-icons/lu";

import { useAppSelector } from "../../app/hooks";
import { useAuth } from "../../context/AuthContext";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { hasPermission, permission } from "../../static/permission";
import { POST } from "../../services/axios.service";
import {
  PIN_UNLOCKED_STORAGE_KEY,
  releaseUnlockPresence,
} from "../../static/pinPresence";
import { getGujaratiRoleName } from "../../static/role";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import {
  iconButtonClass,
  menuContentClass,
  menuItemClass,
  menuSeparatorClass,
} from "./chromeStyles";

const CLOSE_ANIMATION_MS = 150;

const UserMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAuthenticated } = useAuth();
  const loggingUser = useAppSelector(getLoggingUser);

  const [open, setOpen] = useState<boolean>(false);
  const [closing, setClosing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const closeMenu = useCallback(() => {
    if (closeTimer.current) return;

    setClosing(true);
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      setClosing(false);
      closeTimer.current = null;
    }, CLOSE_ANIMATION_MS);
  }, []);

  const toggleMenu = () => {
    if (open) {
      closeMenu();
      return;
    }

    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }

    setClosing(false);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) closeMenu();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, closeMenu]);

  useEffect(() => {
    if (open) closeMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    []
  );

  const handleLogout = async () => {
    setIsLoading(true);

    try {
      await POST("/v1/logout", {})
        .then(async (response: any) => {
          if (response.data.status === "success") {
            await localStorage.removeItem("authToken");
            sessionStorage.removeItem(PIN_UNLOCKED_STORAGE_KEY);
            releaseUnlockPresence();
            await setIsAuthenticated(false);

            navigate(route.login);
          } else {
            toast.error(response.data.message, { ...(toastStyles as any) });
          }
        })
        .catch((error: any) => {
          toast.error(
            error?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
            { ...(toastStyles as any) }
          );
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    setIsLoading(false);
  };

  const roleLabel = loggingUser?.roles?.[0] || "";

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={toggleMenu}
        aria-haspopup="menu"
        aria-expanded={open && !closing}
        className={iconButtonClass}
      >
        <LuUserCircle className="h-4 w-4" />
        <span className="sr-only">User Menu</span>
      </button>

      {open && (
        <div
          role="menu"
          className={`${menuContentClass} ${
            closing ? "animate-menu-out" : "animate-menu-in"
          }`}
        >
          <div className="flex items-center gap-3 p-2 text-sm font-semibold">
            <span className="relative flex h-8 w-8 md:h-10 md:w-10 shrink-0 overflow-hidden rounded-full">
              <span className="flex h-full w-full items-center justify-center rounded-full bg-[#34699A] text-white text-sm md:text-base font-bold">
                {loggingUser?.name?.substring(0, 2)?.toUpperCase() || ""}
              </span>
            </span>

            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-[#113F67] text-sm md:text-base truncate">
                {loggingUser?.name || ""}
              </span>
              <span className="text-xs text-[hsl(215_16%_47%)]">
                ({getGujaratiRoleName(roleLabel) || roleLabel})
              </span>
              <span className="text-xs text-[hsl(215_16%_47%)] flex items-center mt-0.5">
                <LuPhone className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="truncate">{loggingUser?.phone || ""}</span>
              </span>
            </div>
          </div>

          <div className={menuSeparatorClass} />

          <button
            type="button"
            role="menuitem"
            onClick={() => navigate(route.profile)}
            className={menuItemClass}
          >
            <LuUser className="mr-2 h-4 w-4" />
            <span>પ્રોફાઇલ મેનેજ કરો</span>
          </button>

          {hasPermission(loggingUser?.permissions, permission.settingsMenu) && (
            <button
              type="button"
              role="menuitem"
              onClick={() => navigate(route.settings)}
              className={menuItemClass}
            >
              <LuSettings className="mr-2 h-4 w-4" />
              <span>સેટિંગ્સ</span>
            </button>
          )}

          <div className={menuSeparatorClass} />

          <button
            type="button"
            role="menuitem"
            disabled={isLoading}
            onClick={handleLogout}
            className={menuItemClass}
          >
            <LuLogOut className="mr-2 h-4 w-4" />
            <span>{isLoading ? "રાહ જુઓ..." : "લોગઆઉટ"}</span>
          </button>
        </div>
      )}
    </div>
  );
};
export default UserMenu;
